import React from 'react';
import { ArrowRight, Check, ShieldCheck, Star } from 'lucide-react';
import { PurchaseNotification } from './components/PurchaseNotification';
import { TestimonialCard } from './components/TestimonialCard';
export function App() {
  const scrollToCheckout = () => {
    const element = document.getElementById('checkout');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <div className="min-h-screen bg-[#FFFDF7] font-sans text-gray-800 selection:bg-amber-200" data-id="element-0">
      <PurchaseNotification data-id="element-1" />

      {/* Hero Section */}
      <header className="bg-gradient-to-b from-amber-50 to-[#FFFDF7] pt-12 pb-16 px-4" data-id="element-2">
        <div className="max-w-4xl mx-auto text-center" data-id="element-3">
          <div className="inline-block bg-amber-100 text-amber-800 px-4 py-1 rounded-full text-sm font-bold mb-6 tracking-wide uppercase" data-id="element-4">
            Para madres que quieren paz en casa
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 mb-8 leading-tight" data-id="element-5">
            ¿Cómo puede ser que estudiar termine siempre en{' '}
            <span className="text-amber-600" data-id="element-6">gritos y llanto</span>?
          </h1>

          <div className="flex flex-col md:flex-row items-center justify-center gap-8 mt-12" data-id="element-7">
            <div className="w-full max-w-md cursor-pointer transform hover:scale-105 transition-transform duration-300" onClick={scrollToCheckout} data-id="element-8">
              <img src="https://cdn.magicpatterns.com/uploads/3HdsBMEjNvNmmg85QBMwae/COVER_QUE_ESTUDIE_SIN_PELEAR.png" alt="Que Estudie Sin Pelear Libro Digital" className="w-full drop-shadow-2xl rounded-lg" data-id="element-9" />
            </div>
          </div>
        </div>
      </header>

      {/* Sales Letter Content */}
      <main className="max-w-3xl mx-auto px-6 py-8" data-id="element-10">
        <div className="prose prose-lg prose-amber mx-auto text-gray-700 leading-relaxed" data-id="element-11">
          <p className="font-medium text-xl text-gray-800" data-id="element-12">
            Hay un momento del día que muchas familias sienten perdido antes de
            que empiece.
          </p>

          <p data-id="element-13">No es a la mañana. No es a la noche.</p>

          <p data-id="element-14">
            Es ese rato en el que alguien dice:{' '}
            <strong data-id="element-15">“Bueno… hay que sentarse a estudiar”.</strong>
          </p>

          <p data-id="element-16">Vos llegás cansada. Tu hijo también.</p>

          <p data-id="element-17">
            Se abre la mochila. Aparece el cuaderno. Se supone que “son solo 20
            minutos”.
          </p>

          <p data-id="element-18">Y en pocos minutos… nada fluye.</p>

          <div className="bg-red-50 border-l-4 border-red-400 p-6 my-8 rounded-r-lg" data-id="element-19">
            <p className="italic m-0 text-red-800" data-id="element-20">
              Él no arranca. Pide agua. Va al baño. Se para. Mira cualquier cosa
              menos la hoja. Dice que no entiende. Dice que es aburrido. Dice
              que mañana lo hace.
            </p>
          </div>

          <p data-id="element-21">
            Vos respirás hondo. Intentás mantener la calma. Probás con
            paciencia. Después con firmeza. Después con insistencia.
          </p>

          <p data-id="element-22">Y sin darte cuenta, otra vez:</p>

          <p className="text-xl font-bold text-gray-900 text-center my-8" data-id="element-23">
            Excusas. Tensión. Gritos. Llanto. Enojo. Culpa.
          </p>

          <p data-id="element-24">Y esa sensación fea que queda flotando cuando todo termina mal:</p>

          <p data-id="element-25">
            “¿Cómo puede ser que estudiar termine siempre así?” “¿Qué estoy
            haciendo mal?” “¿Por qué algo tan básico se volvió una pelea
            diaria?”
          </p>

          <p data-id="element-26">
            Si esto se parece demasiado a tu casa, frená un segundo y leé esto
            con atención:
          </p>

          <div className="bg-amber-50 p-8 rounded-xl border border-amber-100 my-10 shadow-sm" data-id="element-27">
            <h3 className="text-2xl font-bold text-amber-800 mt-0 mb-4 text-center" data-id="element-28">
              No sos mala madre.
            </h3>
            <p className="text-center mb-0" data-id="element-29">
              No sos autoritaria. No sos blanda. No fracasaste. Y tu hijo no es
              vago ni desinteresado.
            </p>
          </div>

          <p data-id="element-30">Lo que está fallando no sos vos. Lo que está fallando no es él.</p>

          <p data-id="element-31">
            Lo que está fallando es algo que casi nadie ve… pero que define
            absolutamente todo:
          </p>

          <p className="font-bold text-lg" data-id="element-32">
            El sistema invisible con el que intentaron que estudiar funcione.
          </p>

          <p data-id="element-33">Y nadie te lo explicó.</p>

          <p data-id="element-34">
            La mayoría de las mamás que llegan acá dicen lo mismo, aunque lo
            expresen distinto:
          </p>

          <ul className="space-y-4 list-none pl-0 my-8" data-id="element-35">
            <li className="flex items-start gap-3" data-id="element-36">
              <span className="text-amber-500 text-xl" data-id="element-37">💬</span>
              <span data-id="element-38">
                “Ya no sé cómo pedirle que estudie sin terminar gritándole.”
              </span>
            </li>
            <li className="flex items-start gap-3" data-id="element-39">
              <span className="text-amber-500 text-xl" data-id="element-40">💬</span>
              <span data-id="element-41">“Me duele que estudiar nos separe.”</span>
            </li>
            <li className="flex items-start gap-3" data-id="element-42">
              <span className="text-amber-500 text-xl" data-id="element-43">💬</span>
              <span data-id="element-44">
                “Todo el día corremos y cuando llega la tarea, no queda
                paciencia.”
              </span>
            </li>
            <li className="flex items-start gap-3" data-id="element-45">
              <span className="text-amber-500 text-xl" data-id="element-46">💬</span>
              <span data-id="element-47">“Quiero ayudarlo… pero no quiero ser su enemiga.”</span>
            </li>
          </ul>

          <p data-id="element-48">
            “Tengo miedo de que esto le quede como algo negativo para siempre.”
          </p>

          <p data-id="element-49">Y ese miedo no es exagerado.</p>

          <p data-id="element-50">
            Porque cuando estudiar se vive todos los días como conflicto,
            presión o fracaso, pasan cosas silenciosas pero profundas:
          </p>

          <p data-id="element-51">
            El chico empieza a asociar estudio con tensión. Se bloquea antes de
            empezar. Baja la motivación. Aparece la evitación. Y la autoestima
            escolar se erosiona.
          </p>

          <p data-id="element-52">
            Mientras vos, por dentro, acumulás cansancio, culpa, dudas y esa
            sensación de que todo depende de vos.
          </p>

          <p data-id="element-53">
            Así se arma un círculo agotador que nadie parece saber cómo cortar.
          </p>

          {/* First CTA */}
          <div className="my-12 text-center" data-id="element-54">
            <button onClick={scrollToCheckout} className="bg-amber-500 hover:bg-amber-600 text-white font-bold py-4 px-8 rounded-full shadow-lg transform transition hover:-translate-y-1 text-lg flex items-center justify-center gap-2 mx-auto w-full md:w-auto" data-id="element-55">
              Sí, quiero terminar con las peleas
              <ArrowRight className="w-5 h-5" data-id="element-56" />
            </button>
          </div>

          <p data-id="element-57">Tal vez ya probaste de todo.</p>

          <p data-id="element-58">
            Castigos. Premios. Horarios rígidos. “Primero tarea, después juego”.
            Sentarte al lado explicándole todo. Dejarlo solo para que “aprenda
            responsabilidad”. Videos, apps, consejos.
          </p>

          <p data-id="element-59">Y por momentos funciona… pero dura poco.</p>

          <p data-id="element-60">Porque el problema no es la técnica.</p>

          <p data-id="element-61">
            El problema es que casi todas esas soluciones atacan el estudio,
            pero ignoran el estado con el que tu hijo llega a estudiar.
          </p>

          <p data-id="element-62">Después de un día largo, tu hijo no llega en blanco.</p>

          <p data-id="element-63">Llega con escuela. Exigencias. Estímulos. Ruido. Cansancio.</p>

          <p data-id="element-64">
            Pretender que se siente, se concentre y rinda como si nada es como
            pedirle a alguien que corra con la batería en rojo.
          </p>

          <p className="font-bold" data-id="element-65">No es mala voluntad. Es desgaste.</p>

          <p data-id="element-66">
            Y cuando ese desgaste no se reconoce ni se ordena, aparece la pelea.
          </p>

          <p data-id="element-67">
            La mayoría de las familias pregunta: “¿Cómo logro que haga la
            tarea?”
          </p>

          <p data-id="element-68">La pregunta correcta es otra:</p>

          <h3 className="text-2xl font-bold text-amber-800 text-center my-8" data-id="element-69">
            ¿Cómo hacemos para que llegue a estudiar en un estado en el que
            pueda estudiar?
          </h3>

          <p data-id="element-70">
            Cuando cambiás eso, baja la resistencia, baja la discusión y el
            hábito empieza a construirse con menos fricción.
          </p>

          <p data-id="element-71">No perfecto. Real.</p>

          <p data-id="element-72">Imaginate que llega la hora del estudio y pasa esto:</p>

          <div className="bg-green-50 p-6 rounded-lg border border-green-100 my-8" data-id="element-73">
            <ul className="space-y-3 list-none pl-0 m-0" data-id="element-74">
              <li className="flex items-center gap-3" data-id="element-75">
                <Check className="text-green-500 w-6 h-6 flex-shrink-0" data-id="element-76" />
                <span data-id="element-77">
                  Tu hijo sabe qué tiene que hacer. No lo vive como castigo. No
                  lo vive como amenaza.
                </span>
              </li>
              <li className="flex items-center gap-3" data-id="element-78">
                <Check className="text-green-500 w-6 h-6 flex-shrink-0" data-id="element-79" />
                <span data-id="element-80">
                  Vos no tenés que rogar. No tenés que gritar. No tenés que
                  explicar diez veces.
                </span>
              </li>
              <li className="flex items-center gap-3" data-id="element-81">
                <Check className="text-green-500 w-6 h-6 flex-shrink-0" data-id="element-82" />
                <span data-id="element-83">
                  Hay una estructura clara. Hay un clima distinto. Hay menos
                  resistencia.
                </span>
              </li>
            </ul>
          </div>

          <p data-id="element-84">
            Estudiar deja de ser una guerra diaria y pasa a ser una rutina
            posible.
          </p>

          <p data-id="element-85">Y ahí vuelve a aparecer lo más importante: el aprendizaje.</p>

          <p data-id="element-86">Por eso existe:</p>

          <div className="text-center my-10 bg-white p-8 rounded-2xl shadow-xl border-2 border-amber-100" data-id="element-87">
            <h2 className="text-3xl md:text-4xl font-black text-amber-600 mb-2" data-id="element-88">
              QUE ESTUDIE SIN PELEAR
            </h2>
            <p className="text-xl text-gray-600 font-medium" data-id="element-89">
              Cómo crear hábitos de estudio en casa, aunque hoy todo sea gritos,
              excusas y cansancio.
            </p>
            <div className="mt-6 cursor-pointer" onClick={scrollToCheckout} data-id="element-90">
              <img src="https://cdn.magicpatterns.com/uploads/3HdsBMEjNvNmmg85QBMwae/COVER_QUE_ESTUDIE_SIN_PELEAR.png" alt="Portada del libro" className="w-2/3 mx-auto rounded shadow-lg" data-id="element-91" />
            </div>
          </div>

          <p data-id="element-92">
            Esto no es un método escolar. No es un curso para que tu hijo rinda
            más. No es teoría. No es exigencia.
          </p>

          <p data-id="element-93">
            Es una guía clara, humana y realista para ordenar el momento del
            estudio sin romper el vínculo.
          </p>

          <p data-id="element-94">
            Funciona incluso si hoy tu hijo se resiste, vos estás agotada, no
            sabés cómo empezar o sentís que ya probaste de todo.
          </p>

          <p data-id="element-95">
            Porque no se basa en control. Se basa en estructura emocional y
            hábitos simples.
          </p>

          <p data-id="element-96">
            La idea nació en casas reales. Con mochilas tiradas en el piso. Con
            chicos cansados. Con madres que piensan: “Si sigo así, estudiar va a
            romper algo entre nosotros.”
          </p>

          <p data-id="element-97">Y ahí apareció la pregunta que cambia todo:</p>

          <p className="font-bold italic" data-id="element-98">
            ¿Y si el problema no fuera el estudio, sino cómo llegamos a
            estudiar?
          </p>

          <p data-id="element-99">Cuando cambiás eso, todo empieza a acomodarse.</p>

          <h3 className="text-2xl font-bold mt-12 mb-6" data-id="element-100">
            Dentro de QUE ESTUDIE SIN PELEAR vas a encontrar:
          </h3>

          <div className="grid md:grid-cols-2 gap-4 my-8" data-id="element-101">
            {['Cómo crear una rutina de estudio posible, no ideal.', 'Qué errores comunes generan resistencia sin que te des cuenta.', 'Cómo dejar de explicar de más.', 'Cómo marcar límites sin gritar.', 'Cómo ayudar sin hacerte cargo de todo.', 'Qué hacer cuando un día sale mal sin tirar todo por la borda.'].map((item, i) => <div key={i} className="bg-white p-4 rounded shadow-sm border-l-4 border-amber-400" data-id="element-102">
                <p className="m-0 text-sm md:text-base font-medium" data-id="element-103">{item}</p>
              </div>)}
          </div>

          <p data-id="element-104">
            Todo en lenguaje simple, pensado para madres cansadas, con poco
            tiempo y mucha carga encima.
          </p>

          <p data-id="element-105">
            Estudiar hoy es como intentar armar un rompecabezas cuando todos
            están apurados. Primero hay que bajar el ruido y ordenar la mesa.
          </p>

          <p data-id="element-106">
            Es como manejar en una ciudad sin semáforos. No importa cuánto
            sepas: sin orden, todo es choque.
          </p>

          <p data-id="element-107">Este ebook te muestra cómo cerrar esa puerta sin pelear.</p>

          <p data-id="element-108">No te promete hijos perfectos ni tareas mágicas.</p>

          <p data-id="element-109">
            Te promete algo mucho más valioso: menos conflicto, más claridad y
            un vínculo que no se rompe por la tarea.
          </p>

          <p data-id="element-110">
            Porque cuando la pelea desaparece, el hábito empieza a construirse
            solo.
          </p>
        </div>
      </main>

      {/* Bonuses Section */}
      <section className="bg-amber-900 text-amber-50 py-16 px-4" data-id="element-111">
        <div className="max-w-5xl mx-auto" data-id="element-112">
          <div className="text-center mb-12" data-id="element-113">
            <span className="bg-red-500 text-white px-4 py-1 rounded-full text-sm font-bold uppercase tracking-wider animate-pulse" data-id="element-114">
              Gratis por el día de hoy
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mt-6 mb-4" data-id="element-115">
              Llevate estos 3 Regalos Exclusivos
            </h2>
            <p className="text-amber-200 text-lg" data-id="element-116">
              Herramientas prácticas para momentos críticos
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-117">
            {[{
            img: 'https://cdn.magicpatterns.com/uploads/ac7HC6ezsG2LXa76CvHPEB/BONO_1_EBOOK2.png',
            title: 'Guía de Emergencia',
            desc: 'Para el momento crítico'
          }, {
            img: 'https://cdn.magicpatterns.com/uploads/rH9g9Z7B8VKh1NgSoHBHDA/BONO_2_EBOOK_2.png',
            title: 'Checklist Pre-Estudio',
            desc: '5 minutos para preparar el terreno'
          }, {
            img: 'https://cdn.magicpatterns.com/uploads/qjec9cw1MThtKpsm9ysLpb/bono_3_ebook_2.png',
            title: 'Mini Guía: Cómo Ayudar',
            desc: 'Sin hacerte cargo de todo'
          }].map((bonus, idx) => <div key={idx} className="bg-white/5 p-4 rounded-xl border border-white/10 hover:bg-white/10 transition cursor-pointer" onClick={scrollToCheckout} data-id="element-118">
                <img src={bonus.img} alt={bonus.title} className="w-full rounded-lg mb-4 shadow-lg" data-id="element-119" />
                <h3 className="text-xl font-bold text-amber-100 mb-2" data-id="element-120">
                  {bonus.title}
                </h3>
                <p className="text-amber-200/80 text-sm" data-id="element-121">{bonus.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 px-4 bg-white" data-id="element-122">
        <div className="max-w-6xl mx-auto" data-id="element-123">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12" data-id="element-124">
            Lo que dicen otras mamás
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-125">
            <TestimonialCard name="María" location="Buenos Aires" age={38} childrenInfo="Santi (9 años)" content="Sentía que me transformaba en un monstruo cada vez que nos sentábamos. Este libro me hizo ver que el problema no era la tarea, sino cómo llegábamos a ella. Ayer estudiamos 40 minutos sin un solo grito. Para mí es un milagro." data-id="element-126" />
            <TestimonialCard name="Laura" location="México DF" age={41} childrenInfo="Valentina (11 años)" content="Ya había probado castigos, premios, quitarle la tablet... nada funcionaba más de dos días. Lo que más me gustó es que no te pide ser una madre perfecta, te da herramientas para la vida real. El checklist del bono 2 es oro puro." data-id="element-127" />
            <TestimonialCard name="Carolina" location="Bogotá" age={36} childrenInfo="Mateo (8 años)" content="Me la pasaba explicando y haciendo la tarea por él porque me desesperaba. Entendí cómo ayudar sin resolverle todo. Ahora él prepara su mesa solo y yo puedo tomarme un café mientras él empieza. Recuperé mi paz mental." data-id="element-128" />
            <TestimonialCard name="Gabriela" location="Santiago" age={39} childrenInfo="Lucas (10 años) y Sofi (7 años)" content="Con dos hijos en primaria, las tardes eran un caos de gritos cruzados. Implementamos la rutina posible que sugiere el libro y el cambio de clima en casa es total. No es magia, pero se siente así." data-id="element-129" />
            <TestimonialCard name="Patricia" location="Lima" age={35} childrenInfo="Andrés (7 años)" content="Llegaba del trabajo agotada y pelear por la tarea era lo último que necesitaba. Este libro me dio permiso para bajar la exigencia y conectar mejor. Mi hijo está más tranquilo y yo también." data-id="element-130" />
          </div>
        </div>
      </section>

      {/* Checkout Section */}
      <section id="checkout" className="py-20 px-4 bg-gradient-to-br from-amber-50 to-orange-50" data-id="element-131">
        <div className="max-w-3xl mx-auto bg-white rounded-3xl shadow-2xl overflow-hidden border border-amber-100" data-id="element-132">
          <div className="bg-amber-500 p-4 text-center" data-id="element-133">
            <p className="text-white font-bold text-lg animate-pulse" data-id="element-134">
              ¡OFERTA ESPECIAL POR TIEMPO LIMITADO!
            </p>
          </div>

          <div className="p-8 md:p-12 text-center" data-id="element-135">
            <h2 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-136">
              Que Estudie Sin Pelear
            </h2>
            <p className="text-gray-500 mb-8" data-id="element-137">
              + Todos los bonos de regalo incluidos
            </p>

            <div className="flex justify-center items-center gap-4 mb-8" data-id="element-138">
              <span className="text-gray-400 line-through text-2xl" data-id="element-139">
                $29.97
              </span>
              <span className="text-6xl font-black text-amber-600 tracking-tight" data-id="element-140">
                $9.97
              </span>
            </div>

            <a href="Link de tu checkout" className="block w-full bg-orange-500 hover:bg-orange-600 text-white font-bold text-2xl py-6 px-8 rounded-xl shadow-xl transform transition hover:-translate-y-1 hover:shadow-2xl mb-6" data-id="element-141">
              QUIERO MI COPIA AHORA
            </a>

            <div className="flex flex-col md:flex-row justify-center items-center gap-6 text-sm text-gray-500 mt-8" data-id="element-142">
              <div className="flex items-center gap-2" data-id="element-143">
                <ShieldCheck className="w-5 h-5 text-green-500" data-id="element-144" />
                <span data-id="element-145">Pago 100% Seguro</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-146">
                <Check className="w-5 h-5 text-green-500" data-id="element-147" />
                <span data-id="element-148">Acceso Inmediato al Email</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-149">
                <Star className="w-5 h-5 text-green-500" data-id="element-150" />
                <span data-id="element-151">Satisfacción Garantizada</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final Closing */}
      <footer className="bg-[#FFFDF7] py-16 px-4 text-center" data-id="element-152">
        <div className="max-w-2xl mx-auto prose prose-amber" data-id="element-153">
          <p className="text-lg" data-id="element-154">
            Si hoy sentís que el estudio te drena, que te anticipás al
            conflicto, que ya te cansaste de discutir y querés ayudar sin
            lastimar, este material es para vos.
          </p>

          <p data-id="element-155">
            <strong data-id="element-156">QUE ESTUDIE SIN PELEAR</strong> es un ebook práctico, paso a
            paso, sin tecnicismos, sin culpas, pensado para aplicar desde el
            primer día.
          </p>

          <p data-id="element-157">
            Lo leés a tu ritmo. Lo aplicás con lo que ya tenés. Sin cambiar
            quién sos.
          </p>

          <p data-id="element-158">
            Si querés que el estudio deje de ser un campo de batalla. Si querés
            volver a sentir calma en ese momento del día. Si querés ayudar sin
            pelear.
          </p>

          <p className="font-bold text-xl text-amber-800" data-id="element-159">
            Este es tu próximo paso.
          </p>

          <div className="mt-8" data-id="element-160">
            <button onClick={scrollToCheckout} className="text-amber-600 font-bold hover:text-amber-800 underline decoration-2 underline-offset-4" data-id="element-161">
              Accedé ahora a QUE ESTUDIE SIN PELEAR y empezá a construir hábitos
              de estudio en casa sin romper el vínculo más importante que tenés.
            </button>
          </div>

          <p className="text-xs text-gray-400 mt-12" data-id="element-162">
            © {new Date().getFullYear()} Que Estudie Sin Pelear. Todos los
            derechos reservados.
          </p>
        </div>
      </footer>
    </div>;
}